import { Button, Dialog, surface, Text } from "@nudgebox/ui";
import { AddToHomeScreenIcon, DeviceIcon, DownloadIcon, MoreVerticalIcon, ShareIcon } from "@nudgebox/ui/icons";
import "@nudgebox/ui/tokens.stylex";
import { color, fontWeight, radius, space } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useEffect } from "react";
import { dismissInstall, promptInstall, setInstallDialogOpen, useInstallDialogOpen, useInstallState, wasInstallDismissed } from "@/lib/install.ts";

/** Wait a beat after landing before offering the install, so it doesn't greet a loading page. */
const AUTO_OPEN_DELAY_MS = 2500;
/** Offered at most once per page load, however often the shell remounts. */
let autoOpened = false;

/**
 * "Add to home screen" dialog for phones and tablets. Opens by itself once (until dismissed) and
 * from the account menu. Android opens the browser's own install sheet; iOS can't be installed
 * programmatically, so it walks through Safari's two taps instead.
 */
export function InstallAppDialog() {
  const {
    platform,
    available,
    canPrompt
  } = useInstallState();
  const open = useInstallDialogOpen();

  // Auto-open on iOS, and on Android only once the browser says the app is installable (no event
  // means it's unsupported or already installed, and manual steps would just nag).
  const shouldAutoOpen = available && (platform === "ios" || canPrompt);
  useEffect(() => {
    if (!shouldAutoOpen || autoOpened || wasInstallDismissed()) return;
    const timer = window.setTimeout(() => {
      autoOpened = true;
      setInstallDialogOpen(true);
    }, AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldAutoOpen]);
  if (!available) return null;
  const close = () => {
    dismissInstall();
    setInstallDialogOpen(false);
  };
  const install = () => {
    close();
    void promptInstall();
  };
  const steps: [Step, Step] = platform === "ios" ? [{
    icon: <ShareIcon size={20} />,
    title: <>
                Tap <Strong>Share</Strong>
              </>,
    detail: "In Safari's toolbar, at the bottom or top of the screen."
  }, {
    icon: <AddToHomeScreenIcon size={20} />,
    title: <>
                Choose <Strong>Add to Home Screen</Strong>
              </>,
    detail: "Scroll the share sheet down if you don't see it, then tap Add."
  }] : canPrompt ? [{
    icon: <DownloadIcon size={20} />,
    title: <>
                  Tap <Strong>Install</Strong> below
                </>,
    detail: "Your browser asks to add Nudgebox to your home screen."
  }, {
    icon: <DeviceIcon size={20} />,
    title: <>
                  Confirm with <Strong>Install</Strong>
                </>,
    detail: "Nudgebox opens full screen from your home screen, like any other app."
  }] : [{
    icon: <MoreVerticalIcon size={20} weight="bold" />,
    title: <>
                  Open the browser <Strong>menu</Strong>
                </>,
    detail: "The three dots next to the address bar."
  }, {
    icon: <AddToHomeScreenIcon size={20} />,
    title: <>
                  Tap <Strong>Install app</Strong>
                </>,
    detail: "Some browsers call it Add to home screen."
  }];
  return <Dialog open={open} onOpenChange={next => next ? setInstallDialogOpen(true) : close()} size="sm" title="Install the app in two taps" description="Keep Nudgebox on your home screen: full screen, one tap away." footer={canPrompt ? <>
            <Button onClick={close}>Not now</Button>
            <Button variant="primary" icon={<DownloadIcon size={16} />} onClick={install}>
              Install
            </Button>
          </> : <Button variant="primary" onClick={close}>
            Got it
          </Button>}>
      <ol className="x78zum5 xdt5ytf x4l6xbm x1ghz6dp x1717udv xe8uvvx">
        {steps.map((step, index) => <li key={index} className="x78zum5 x1cy8zhl xpcbtbh">
            <span aria-hidden {...stylex.props(surface.raised, surface.sm, styles.stepIcon)}>
              {step.icon}
            </span>
            <span className="x78zum5 xdt5ytf x92x8s6 xeuugli">
              <Text size="xs" tone="tertiary" weight="medium">
                Step {index + 1}
              </Text>
              <Text size="md">{step.title}</Text>
              <Text size="sm" tone="secondary">
                {step.detail}
              </Text>
            </span>
          </li>)}
      </ol>
    </Dialog>;
}
interface Step {
  icon: ReactNode;
  title: ReactNode;
  detail: string;
}
function Strong({
  children
}: {
  children: ReactNode;
}) {
  return <strong className="x1ivwu1z x9aqtqa">{children}</strong>;
}
const styles = {
  stepIcon: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kzqmXN: "x100vrsf",
    kZKoxP: "x1vqgdyp",
    kaIpWk: "xlfxrz8",
    kMwMTN: "x1rizfjd",
    $$css: true
  }
};